import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

// Types
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { FormOption } from '../../../../../../../types/Form';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useQuestionEnum from '../../../../hooks/useQuestionEnum';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { handleResponseLanguage } from '../../../../../../../i18n';

import { SearchSelect } from '@wegroup/design-system';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import { Inputs } from '../../../../types/Enums';
import useIsInsideModal from '../table/context/useIsInsideModal';

// Local interface for enum_source response structure
interface EnumSourceResponse {
  items: Array<{
    value: string;
    icon_key?: string | null;
    label: {
      nl?: string | null;
      en?: string | null;
      fr?: string | null;
      de?: string | null;
      es?: string | null;
    };
  }>;
}

interface Props {
  question: FlowsQuestion;
}

const SelectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const {
    field: { onBlur, value },
    prefilled,
    error,
  } = useFlowInputComponent({ question });
  const isInsideModal = useIsInsideModal();

  const handleOnChange = (value: unknown) => {
    if (typeof value === 'object' && isEmpty(value))
      onBlur({ name: question.id, value: '' });
    else onBlur({ name: question.id, value: value as string });
  };

  const enumOptions = useQuestionEnum(question);
  const hasEnumSource = Boolean(question.rules.enum_source);

  // State for enum_source options
  const [enumSourceOptions, setEnumSourceOptions] = React.useState<
    FormOption[]
  >([]);

  // Directly fetch enum options from enum_source when user types
  const fetchEnumOptions = React.useCallback(
    async (value: string, isPrefilledSearch = false): Promise<FormOption[]> => {
      if (!hasEnumSource || (!isPrefilledSearch && value.length < 3)) return [];

      try {
        const { data, isError } = await wgApi.get<EnumSourceResponse>(
          `${question.rules.enum_source?.url}?q=${encodeURIComponent(value)}`,
        );

        if (isError) return [];

        return (data.items || []).map(
          (item: EnumSourceResponse['items'][0]): FormOption => ({
            label: handleResponseLanguage(item.label) || item.value,
            value: item.value,
            icon_key: item.icon_key || undefined,
          }),
        );
      } catch {
        return [];
      }
    },
    [hasEnumSource, question.rules.enum_source?.url],
  );

  // Debounce the fetch function
  const [debouncedFetch] = useDebouncedCallback(async (value: string) => {
    const options = await fetchEnumOptions(value);
    setEnumSourceOptions(options);
  }, 300);

  const options = hasEnumSource ? enumSourceOptions : enumOptions;

  // Auto-search for prefilled value when using enum_source
  useEffect(() => {
    if (hasEnumSource && value && enumSourceOptions.length === 0) {
      fetchEnumOptions(value, true).then((options) => {
        setEnumSourceOptions(options);
      });
    }
  }, [hasEnumSource, value, fetchEnumOptions, enumSourceOptions.length]);

  /** When the options change due to conditions, it should double check if the filled in value is still valid
   * If not -> clear the question answer
   */
  useEffect(() => {
    if (
      options.length &&
      value &&
      !options.map(({ value }) => value).includes(value)
    ) {
      handleOnChange(null);
    }
  }, [options.map(({ value }) => value).join()]);

  const getPlaceholder = () => {
    if (hasEnumSource) {
      return t('FLOWS_PRODUCT_TYPE_TO_SEARCH_MIN_3_CHARS');
    }
    return t('DEFAULT_SELECT_PLACEHOLDER');
  };

  const handleSearchChange = (searchValue: string) => {
    if (hasEnumSource) {
      debouncedFetch(searchValue);
    }
  };

  return (
    <SearchSelect
      clearable={hasEnumSource}
      placeholder={getPlaceholder()}
      onChange={handleOnChange}
      data={options}
      error={error?.message}
      value={value}
      prefilled={prefilled}
      // Legacy styling to match flows inputs
      extraStyles={{
        input: {
          height: '45px',
          boxShadow:
            '0 4px 8px rgba(0,0,0,0.04),0 0 2px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)',
          borderColor: prefilled ? '#FFBC0F' : 'rgb(228, 228, 228)',
        },
        dropdown: {
          borderColor: 'rgb(228, 228, 228)',
        },
      }}
      data-test-id={getFlowsDataTestId(
        Inputs.SELECT,
        question,
        'SEARCH-SELECT',
      )}
      isDisabled={question.rules.is_disabled}
      comboboxProps={{ withinPortal: isInsideModal }}
      searchable={hasEnumSource ? true : undefined}
      onSearchChange={hasEnumSource ? handleSearchChange : undefined}
    />
  );
};

export default SelectInputComponent;
